import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Mail, Send, Users, Plus, Trash2, X, RefreshCw, 
  Eye, CheckCircle, AlertCircle, UserPlus, FileText,
  Megaphone, Calendar, Gift, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/api';

// Template category icons
const categoryIcons = {
  announcement: Megaphone,
  schedule: Calendar,
  promotion: Gift,
  engagement: Heart,
  custom: FileText
};

const AdminEmailMarketing = ({ onClose }) => {
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Email compose state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includeBookedUsers, setIncludeBookedUsers] = useState(true);
  const [includeSavedList, setIncludeSavedList] = useState(true);
  const [manualEmails, setManualEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  
  // Recipients preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Manual email list state
  const [savedEmails, setSavedEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  
  // Send state
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/email/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error.message);
    }
  }, []);

  const fetchSavedEmails = useCallback(async () => {
    setLoadingEmails(true);
    try {
      const response = await apiClient.get('/admin/email/manual-list');
      setSavedEmails(response.data);
    } catch (error) {
      console.error('Failed to load manual email list:', error.message);
    } finally {
      setLoadingEmails(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchSavedEmails();
  }, [fetchTemplates, fetchSavedEmails]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template.template_id);
    setSubject(template.subject);
    setMessage(template.message);
    setShowPreview(false);
    setSendResult(null);
  };

  const handleAddManualEmail = () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (manualEmails.some(e => e.email.toLowerCase() === newEmail.toLowerCase())) {
      toast.error('Email already added');
      return;
    }
    
    setManualEmails([...manualEmails, { 
      email: newEmail.trim().toLowerCase(), 
      name: newName.trim() || newEmail.split('@')[0] 
    }]);
    setNewEmail('');
    setNewName('');
  };

  const handleRemoveManualEmail = (email) => {
    setManualEmails(manualEmails.filter(e => e.email !== email));
  };

  const handleSaveEmailToList = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      await apiClient.post('/admin/email/manual-list', {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim() || null
      });
      toast.success('Email added to saved list');
      setNewEmail('');
      setNewName('');
      fetchSavedEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email');
    }
  };

  const handleRemoveSavedEmail = async (emailId) => {
    try {
      await apiClient.delete(`/admin/email/manual-list/${emailId}`);
      toast.success('Email removed from list');
      fetchSavedEmails();
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await apiClient.post('/admin/email/preview', {
        subject,
        message,
        include_booked_users: includeBookedUsers,
        include_saved_list: includeSavedList,
        manual_emails: manualEmails.map(e => e.email)
      });
      setPreviewRecipients(response.data.recipients);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to send this email to ${previewRecipients.length} recipients?`)) {
      return;
    }
    
    setSending(true);
    setSendResult(null);
    
    try {
      const response = await apiClient.post('/admin/email/send', {
        subject: subject.trim(),
        message: message.trim(),
        include_booked_users: includeBookedUsers,
        include_saved_list: includeSavedList,
        manual_emails: manualEmails.map(e => e.email)
      });
      
      setSendResult(response.data);
      toast.success(`Email sent to ${response.data.sent} recipients`);
      
      // Reset form after successful send
      if (response.data.failed === 0) {
        setSubject('');
        setMessage('');
        setManualEmails([]);
        setShowPreview(false);
        setSelectedTemplate(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    setMessage(prev => prev + placeholder);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background-paper border border-white/10 rounded-sm p-8 mb-12"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold">
          EMAIL <span className="text-primary">MARKETING</span>
        </h2>
        <Button variant="ghost" onClick={onClose} data-testid="close-email-marketing-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Template Selection */}
      <div className="mb-6">
        <Label className="text-sm font-medium mb-3 block">
          <FileText className="w-4 h-4 inline mr-2" />
          Choose a Template
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {templates.map((template) => {
            const IconComponent = categoryIcons[template.category] || FileText;
            return (
              <button
                key={template.template_id}
                onClick={() => handleSelectTemplate(template)}
                className={`p-4 rounded-sm border text-left transition-all hover:border-primary/50 ${
                  selectedTemplate === template.template_id 
                    ? 'border-primary bg-primary/10' 
                    : 'border-white/10 bg-background'
                }`}
                data-testid={`template-${template.template_id}`}
              >
                <IconComponent className={`w-5 h-5 mb-2 ${
                  selectedTemplate === template.template_id ? 'text-primary' : 'text-neutral-400'
                }`} />
                <p className="text-sm font-medium line-clamp-2">{template.name}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column: Compose Email */}
        <div className="space-y-6">
          <div>
            <Label htmlFor="email-subject" className="text-sm font-medium">
              Subject Line
            </Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., New Classes Available This Month!"
              className="bg-background border-white/10 mt-1"
              data-testid="email-subject-input"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Use <code className="bg-white/10 px-1 rounded">[First Name]</code> to personalize
            </p>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <Label htmlFor="email-message" className="text-sm font-medium">
                Message
              </Label>
              <div className="flex gap-1 flex-wrap justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertPlaceholder('[First Name]')}
                  className="text-xs h-7 px-2"
                  data-testid="insert-firstname-btn"
                >
                  + [First Name]
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertPlaceholder('[Booking Count]')}
                  className="text-xs h-7 px-2"
                  data-testid="insert-bookingcount-btn"
                >
                  + [Booking Count]
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertPlaceholder('[Last Class]')}
                  className="text-xs h-7 px-2"
                  data-testid="insert-lastclass-btn"
                >
                  + [Last Class]
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertPlaceholder('[Next Class]')}
                  className="text-xs h-7 px-2"
                  data-testid="insert-nextclass-btn"
                >
                  + [Next Class]
                </Button>
              </div>
            </div>
            <Textarea
              id="email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message here. Use placeholders like [First Name], [Last Class], [Next Class], [Booking Count]..."
              className="bg-background border-white/10 mt-1 min-h-[200px] font-mono text-sm"
              rows={10}
              data-testid="email-message-input"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Available placeholders: <code className="bg-white/10 px-1 rounded">[First Name]</code> <code className="bg-white/10 px-1 rounded">[Last Class]</code> <code className="bg-white/10 px-1 rounded">[Next Class]</code> <code className="bg-white/10 px-1 rounded">[Booking Count]</code>
            </p>
          </div>

          {/* Recipients Options */}
          <div className="bg-background p-4 rounded-sm border border-white/10">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Recipients
            </h4>
            
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={includeBookedUsers}
                onChange={(e) => setIncludeBookedUsers(e.target.checked)}
                className="w-5 h-5 rounded border-white/20 bg-background text-primary focus:ring-primary"
                data-testid="include-booked-users-checkbox"
              />
              <div>
                <span className="font-medium">Users who have booked classes</span>
                <p className="text-xs text-neutral-500">All users with at least one completed booking</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={includeSavedList}
                onChange={(e) => setIncludeSavedList(e.target.checked)}
                className="w-5 h-5 rounded border-white/20 bg-background text-primary focus:ring-primary"
                data-testid="include-saved-list-checkbox"
              />
              <div>
                <span className="font-medium">Saved Email List</span>
                <p className="text-xs text-neutral-500">Emails manually added to the marketing list ({savedEmails.length} emails)</p>
              </div>
            </label>

            {/* Manual Emails for this send */}
            {manualEmails.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-neutral-500 mb-2">Additional emails for this send only:</p>
                <div className="flex flex-wrap gap-2">
                  {manualEmails.map((entry) => (
                    <span
                      key={entry.email}
                      className="inline-flex items-center gap-1 bg-primary/20 text-primary px-2 py-1 rounded text-sm"
                    >
                      {entry.email}
                      <button
                        onClick={() => handleRemoveManualEmail(entry.email)}
                        className="hover:text-error"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handlePreview}
              variant="outline"
              className="flex-1"
              disabled={previewLoading}
              data-testid="preview-recipients-btn"
            >
              {previewLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              Preview Recipients
            </Button>
            <Button
              onClick={handleSend}
              className="flex-1 bg-primary hover:bg-primary-hover"
              disabled={sending || !showPreview || previewRecipients.length === 0}
              data-testid="send-email-btn"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>

          {/* Send Result */}
          {sendResult && (
            <div className={`p-4 rounded-sm border ${
              sendResult.failed === 0 
                ? 'bg-success/10 border-success/30' 
                : 'bg-warning/10 border-warning/30'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {sendResult.failed === 0 ? (
                  <CheckCircle className="w-5 h-5 text-success" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-warning" />
                )}
                <span className="font-medium">Campaign Complete</span>
              </div>
              <p className="text-sm">
                Sent: <span className="font-bold text-success">{sendResult.sent}</span> | 
                Failed: <span className="font-bold text-error">{sendResult.failed}</span> | 
                Total: {sendResult.total_recipients}
              </p>
            </div>
          )}
        </div>

        {/* Right Column: Manage Emails & Preview */}
        <div className="space-y-6">
          {/* Add Email Form */}
          <div className="bg-background p-4 rounded-sm border border-white/10">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary" />
              Add Email
            </h4>
            <div className="space-y-3">
              <Input
                placeholder="Email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="bg-background border-white/10"
                data-testid="new-email-input"
              />
              <Input
                placeholder="Name (optional)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="bg-background border-white/10"
                data-testid="new-name-input"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleAddManualEmail}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid="add-email-temp-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add to This Send
                </Button>
                <Button
                  onClick={handleSaveEmailToList}
                  size="sm"
                  className="flex-1 bg-success hover:bg-success/80"
                  data-testid="save-email-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Save to List
                </Button>
              </div>
            </div>
          </div>

          {/* Saved Email List */}
          <div className="bg-background p-4 rounded-sm border border-white/10">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                Saved Email List
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSavedEmails}
                disabled={loadingEmails}
              >
                <RefreshCw className={`w-4 h-4 ${loadingEmails ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            {savedEmails.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-4">
                No emails saved yet. Use &quot;Save to List&quot; to add emails that will be included in all future sends.
              </p>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {savedEmails.map((entry) => (
                  <div
                    key={entry.email_id}
                    className="flex justify-between items-center p-2 bg-background-paper rounded border border-white/5"
                  >
                    <div>
                      <p className="text-sm font-medium">{entry.name}</p>
                      <p className="text-xs text-neutral-500">{entry.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-error hover:text-error"
                      onClick={() => handleRemoveSavedEmail(entry.email_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recipients Preview */}
          {showPreview && (
            <div className="bg-background p-4 rounded-sm border border-white/10">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" />
                Recipients Preview ({previewRecipients.length})
              </h4>
              
              {/* Preview personalization example */}
              {previewRecipients.length > 0 && message.includes('[First Name]') && (
                <div className="bg-primary/10 border border-primary/30 rounded-sm p-3 mb-3">
                  <p className="text-xs text-primary mb-1">Preview for first recipient:</p>
                  <p className="text-sm">
                    Subject: <strong>{subject.replace('[First Name]', previewRecipients[0].name?.split(' ')[0] || 'there')}</strong>
                  </p>
                </div>
              )}
              
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {previewRecipients.map((recipient, index) => (
                  <div
                    key={`${recipient.email}-${index}`}
                    className="flex justify-between items-center p-2 bg-background-paper rounded border border-white/5 text-sm"
                  >
                    <div>
                      <p className="font-medium">{recipient.name || 'Unknown'}</p>
                      <p className="text-xs text-neutral-500">{recipient.email}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      recipient.source === 'booked' 
                        ? 'bg-success/20 text-success' 
                        : recipient.source === 'manual_list'
                        ? 'bg-primary/20 text-primary'
                        : 'bg-secondary/20 text-secondary'
                    }`}>
                      {recipient.source === 'booked' ? 'Booked' : recipient.source === 'manual_list' ? 'Saved' : 'Manual'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdminEmailMarketing;
